import axios from "axios";

// Client HTTP de la console. La session admin est un cookie HttpOnly posé par
// l'API : le JS ne lit ni n'écrit jamais ce cookie, d'où withCredentials.
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

export const adminApi = axios.create({ baseURL: BASE, withCredentials: true, timeout: 25000 });

// Familles d'erreurs exposées aux écrans. Chaque appelant décide de l'affichage.
export const ERR = {
  SESSION: "session",
  PERMISSION: "permission",
  REAUTH: "reauth",
  NOT_FOUND: "not_found",
  INVALID: "invalid",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  SERVER: "server",
  NETWORK: "network",
  CANCELLED: "cancelled",
  UNKNOWN: "unknown",
};

const MESSAGES = {
  [ERR.SESSION]: "Votre session d'administration a expiré. Reconnectez-vous.",
  [ERR.PERMISSION]: "Vous n'avez pas la permission d'effectuer cette action.",
  [ERR.REAUTH]: "Cette action exige une ré-authentification.",
  [ERR.NOT_FOUND]: "Ressource introuvable.",
  [ERR.INVALID]: "La requête a été refusée : vérifiez les champs saisis.",
  [ERR.CONFLICT]: "La ressource a changé entre-temps. Rechargez avant de réessayer.",
  [ERR.RATE_LIMIT]: "Trop de requêtes. Patientez avant de réessayer.",
  [ERR.SERVER]: "L'API d'administration est indisponible. Réessayez dans un instant.",
  [ERR.NETWORK]: "Serveur injoignable. Vérifiez votre connexion.",
  [ERR.CANCELLED]: "Requête annulée.",
  [ERR.UNKNOWN]: "Une erreur inattendue est survenue.",
};

// Erreur normalisée. On n'utilise pas `class extends Error` : selon la cible de
// compilation, l'héritage natif se perd et `instanceof` devient faux.
export function adminError(kind, extra = {}) {
  const err = new Error(extra.message || MESSAGES[kind] || MESSAGES[ERR.UNKNOWN]);
  err.name = "AdminError";
  err.kind = kind;
  err.status = extra.status ?? null;
  err.code = extra.code ?? null;
  err.requestId = extra.requestId ?? null;
  err.retryAfter = extra.retryAfter ?? null;
  err.permission = extra.permission ?? null;
  err.fields = extra.fields ?? null;
  return err;
}

export const isAdminError = err => err?.name === "AdminError";
export const errorMessage = (err, fallback = MESSAGES[ERR.UNKNOWN]) =>
  (isAdminError(err) ? err.message : err?.message) || fallback;

/* ---------- Jeton de ré-authentification ---------- */
// Volontairement en mémoire seule et à durée de vie courte : un jeton de
// ré-auth stocké dans localStorage annulerait l'intérêt de la ré-auth.
let reauthState = { token: null, expiresAt: 0 };
let reauthResolver = null;

export function setReauthToken(token, expiresInSeconds = 300) {
  reauthState = { token, expiresAt: Date.now() + expiresInSeconds * 1000 };
}
export function clearReauthToken() {
  reauthState = { token: null, expiresAt: 0 };
}
export function hasReauthToken() {
  return Boolean(reauthState.token) && reauthState.expiresAt > Date.now();
}
// Le fournisseur de contexte enregistre ici la façon d'obtenir un jeton (dialogue).
export function setReauthResolver(fn) {
  reauthResolver = fn;
}

/* ---------- Abonnés aux pertes de session ---------- */
const sessionListeners = new Set();
export function onSessionLost(fn) {
  sessionListeners.add(fn);
  return () => sessionListeners.delete(fn);
}

/* ---------- Intercepteurs ---------- */
adminApi.interceptors.request.use(config => {
  if (hasReauthToken()) config.headers["X-Admin-Reauth"] = reauthState.token;
  return config;
});

function parseRetryAfter(value) {
  if (!value) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, Math.round(seconds));
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : Math.max(0, Math.round((at - Date.now()) / 1000));
}

function detailOf(data) {
  const detail = data?.detail ?? data?.message;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg).filter(Boolean).join(". ") || null;
  if (detail && typeof detail === "object") return detail.message || null;
  return null;
}

function classify(error) {
  if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") return adminError(ERR.CANCELLED);
  const res = error?.response;
  if (!res) return adminError(ERR.NETWORK, { code: error?.code || null });

  const data = res.data || {};
  const code = data.code || data.detail?.code || null;
  const requestId = res.headers?.["x-request-id"] || data.request_id || null;
  const message = detailOf(data);
  const base = { status: res.status, code, requestId, message };

  // Une ré-auth peut être exigée sur un 401, un 403 ou un 428 selon l'API :
  // on se fie au code applicatif, pas au seul statut.
  if (code === "reauth_required" || res.status === 428) {
    return adminError(ERR.REAUTH, { ...base, message: message || MESSAGES[ERR.REAUTH] });
  }
  switch (res.status) {
    case 401: return adminError(ERR.SESSION, base);
    case 403: return adminError(ERR.PERMISSION, { ...base, permission: data.permission || data.detail?.permission || null });
    case 404: return adminError(ERR.NOT_FOUND, base);
    case 409: return adminError(ERR.CONFLICT, base);
    case 422:
    case 400: return adminError(ERR.INVALID, { ...base, fields: data.fields || null });
    case 429: return adminError(ERR.RATE_LIMIT, { ...base, retryAfter: parseRetryAfter(res.headers?.["retry-after"]) });
    default: break;
  }
  if (res.status >= 500) return adminError(ERR.SERVER, base);
  return adminError(ERR.UNKNOWN, base);
}

adminApi.interceptors.response.use(
  response => response,
  error => {
    const normalised = classify(error);
    if (normalised.kind === ERR.SESSION) {
      clearReauthToken();
      sessionListeners.forEach(fn => fn(normalised));
    }
    if (normalised.kind === ERR.REAUTH) clearReauthToken();
    return Promise.reject(normalised);
  },
);

/* ---------- Rejeu après ré-authentification ---------- */
// `run` doit être idempotent : il est rejoué une fois, et une seule, après
// obtention du jeton. Le jeton part ensuite dans l'en-tête X-Admin-Reauth.
export async function withReauth(run, resolve = null) {
  try {
    return await run();
  } catch (err) {
    if (err?.kind !== ERR.REAUTH) throw err;
    const ask = resolve || reauthResolver;
    if (!ask) throw err;
    const granted = await ask(err);
    if (!granted) throw err;
    const token = typeof granted === "string" ? granted : granted.token;
    if (!token) throw err;
    setReauthToken(token, typeof granted === "string" ? 300 : granted.expires_in ?? 300);
    return run();
  }
}

/* ---------- Raccourcis ---------- */
export const get = (url, config) => adminApi.get(url, config).then(r => r.data);
export const post = (url, body, config) => adminApi.post(url, body, config).then(r => r.data);
export const patch = (url, body, config) => adminApi.patch(url, body, config).then(r => r.data);
export const put = (url, body, config) => adminApi.put(url, body, config).then(r => r.data);
export const del = (url, config) => adminApi.delete(url, config).then(r => r.data);

// Liste paginée : accepte indifféremment un curseur ou un décalage.
export function list(url, query = {}, config = {}) {
  const params = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || (Array.isArray(value) && !value.length)) return;
    params[key] = Array.isArray(value) ? value.join(",") : value;
  });
  return get(url, { ...config, params });
}
